import React, { useState } from 'react';
import {
    Modal,
    FormLayout,
    TextField,
    Select,
} from '@shopify/polaris';

import { validateNumeric } from '../../utils/inputSecurity';
import apiClient from '../../utils/api';

export const CommissionOverride = ({
    brand,
    availableMarketplaceTags,
    availableMarketplacesLoading,
    saving,
    showCommissionModal,
    setShowCommissionModal,
    setSaving,
    setError,
    fetchBrandData,
}) => {
    const [newOverrideTag, setNewOverrideTag] = useState('');
    const [newOverrideRate, setNewOverrideRate] = useState('');

    const addCommissionOverride = async () => {
        if (!brand) return;

        // Validate marketplace selection
        if (!newOverrideTag || newOverrideTag.trim() === '') {
            setError('Please select a marketplace');
            return;
        }

        // Validate commission rate
        const rateValidation = validateNumeric(newOverrideRate, {
            min: 0,
            max: 100,
            allowNegative: false
        });

        if (!rateValidation.isValid) {
            setError(`Commission rate error: ${rateValidation.error}`);
            return;
        }

        try {
            setSaving(true);
            await apiClient.createCommissionOverride(brand.id, {
                orderTag: newOverrideTag, // Already clean from dropdown
                commissionRate: rateValidation.sanitized / 100
            });

            await fetchBrandData(); // Refresh data after add
            setShowCommissionModal(false);
            setNewOverrideTag('');
            setNewOverrideRate('');
        } catch (err) {
            setError(err.message || 'Failed to add commission override');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            open={showCommissionModal}
            onClose={() => {
                setShowCommissionModal(false);
                setNewOverrideTag('');
                setNewOverrideRate('');
            }}
            title="Add Commission Override"
            primaryAction={{
                content: 'Add Override',
                onAction: addCommissionOverride,
                loading: saving,
                disabled: !newOverrideTag || !newOverrideRate
            }}
            secondaryActions={[{
                content: 'Cancel',
                onAction: () => {
                    setShowCommissionModal(false);
                    setNewOverrideTag('');
                    setNewOverrideRate('');
                }
            }]}
        >
            <Modal.Section>
                <FormLayout>
                    <Select
                        label="Marketplace"
                        options={[
                            { label: 'Select marketplace...', value: '' },
                            ...availableMarketplaceTags
                                .filter(marketplace => !brand?.commissionOverrides?.some(
                                    override => override.orderTag === marketplace
                                ))
                                .map(marketplace => ({
                                    label: marketplace,
                                    value: marketplace
                                }))
                        ]}
                        value={newOverrideTag}
                        onChange={setNewOverrideTag}
                        helpText={(() => {
                            if (availableMarketplacesLoading) return "Loading available marketplaces...";

                            const unconfiguredMarketplaces = availableMarketplaceTags.filter(
                                marketplace => !brand?.commissionOverrides?.some(
                                    override => override.orderTag === marketplace
                                )
                            );

                            if (unconfiguredMarketplaces.length > 0) {
                                return "Choose a marketplace for this commission override";
                            } else if (availableMarketplaceTags.length > 0) {
                                return "All available marketplaces already have commission overrides configured";
                            } else {
                                return "No marketplaces detected for this brand";
                            }
                        })()}
                        disabled={(() => {
                            if (availableMarketplacesLoading) return true;

                            const unconfiguredMarketplaces = availableMarketplaceTags.filter(
                                marketplace => !brand?.commissionOverrides?.some(
                                    override => override.orderTag === marketplace
                                )
                            );

                            return unconfiguredMarketplaces.length === 0;
                        })()}
                    />
                    <TextField
                        label="Commission Rate (%)"
                        type="number"
                        value={newOverrideRate}
                        onChange={setNewOverrideRate}
                        suffix="%"
                        min="0"
                        max="100"
                        step="0.1"
                        helpText="Commission rate for orders with this tag"
                    />
                </FormLayout>
            </Modal.Section>
        </Modal>
    )
}